import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FaFileAlt, FaFileArchive, FaFilePdf, FaFileWord } from "react-icons/fa";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import { ASSET_URI } from "../lib/utils";
import ResourceFilename from "./ResourceFilename";

const formatFileSize = (size) => {
  const numericSize = Number(size || 0);
  if (!numericSize) return "";
  if (numericSize < 1024) return `${numericSize} B`;
  if (numericSize < 1024 * 1024) return `${(numericSize / 1024).toFixed(1)} KB`;
  if (numericSize < 1024 * 1024 * 1024) {
    return `${(numericSize / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(numericSize / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const getFileIcon = (filePath = "", mimeType = "") => {
  const normalizedPath = String(filePath).toLowerCase();
  const normalizedMimeType = String(mimeType).toLowerCase();

  if (normalizedMimeType.includes("pdf") || normalizedPath.endsWith(".pdf")) return FaFilePdf;
  if (normalizedMimeType.includes("word") || normalizedPath.endsWith(".doc") || normalizedPath.endsWith(".docx")) return FaFileWord;
  if (normalizedMimeType.includes("zip") || normalizedMimeType.includes("archive") || normalizedPath.endsWith(".zip")) return FaFileArchive;
  return FaFileAlt;
};

const CourseResourceCard = ({ file, onDownload, downloadingFileId }) => {
  const { t } = useTranslation();
  const fileUrl = `${ASSET_URI}/${file.filePath}`;
  const FileIcon = getFileIcon(file.filePath, file.mimeType);
  const fileLabel = file.title || file.originalName || t("Course-resource-file", { defaultValue: "Course file" });
  const originalName = file.originalName || fileLabel;
  const fileSize = formatFileSize(file.size);
  const fileDownloadCount = Number(file.downloadCount || 0);

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex w-full min-w-0 items-start gap-3 sm:flex-1">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-lg shadow-sm">
          <FileIcon className="text-indigo-500" />
        </span>
        <div className="min-w-0 flex-1">
          <ResourceFilename name={fileLabel} originalName={originalName} className="text-sm font-semibold text-slate-800" />
          {(file.originalName || fileSize) && (
            <div className="mt-1 min-w-0 text-xs text-slate-500">
              {file.originalName && <ResourceFilename name={file.originalName} originalName={file.originalName} className="text-xs text-slate-500" />}
              {fileSize && <span className="block">{fileSize}</span>}
            </div>
          )}
          <p className="mt-1 text-xs font-medium text-indigo-500">
            {fileDownloadCount} {t("Downloads-count", { defaultValue: "downloads" })}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap gap-2">
        <a href={fileUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600">
          <FiExternalLink className="text-base" />
          {t("Open-resource", { defaultValue: "Open" })}
        </a>
        <button type="button" onClick={() => onDownload(file)} disabled={downloadingFileId === file._id} className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60">
          {downloadingFileId === file._id ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <FiDownload className="text-base" />}
          {downloadingFileId === file._id
            ? t("Preparing-download", { defaultValue: "Preparing..." })
            : t("Download-resource", { defaultValue: "Download" })}
        </button>
      </div>
    </div>
  );
};

CourseResourceCard.propTypes = {
  file: PropTypes.object.isRequired,
  onDownload: PropTypes.func.isRequired,
  downloadingFileId: PropTypes.string,
};

export default CourseResourceCard;
